import { Injectable, inject } from '@angular/core';

import type { Observable } from 'rxjs';

import type { CandidateReference } from '@core/candidate-types';
import { HttpApiService } from '@core/http-legacy';
import type { Position } from '@core/http-types';
import type { JobBasicReference, OfferBriefReference } from '@core/offers-types';
import type { Placement } from '@core/placements-types';
import type { StageReference } from '@core/stages-types';

export type PlacementHttpReference =
  | CandidateReference
  | OfferBriefReference
  | JobBasicReference
  | StageReference;
export interface PlacementChangeStageParams {
  id: number;
  stageId: number | string;
  runActions?: boolean | null;
  proceed?: boolean;
  position?: number;
  hiredAt?: string;
  jobStartsAt?: string;
  openingId?: number | null;
  workLocationId?: number | null;
  disqualifyReasonId?: number;
  disqualifyRunActions?: boolean;
  removeFromSharingContainers?: boolean;
}
export interface PlacementDisqualifyParams {
  id: number;
  disqualifyReasonId: number | string;
  runActions?: boolean | null;
}
export interface UpdateHiringDetailsParams {
  placementId: number;
  hiredAt: string;
  jobStartsAt: string;
  openingId: number | null;
  workLocationId: number | null;
}

@Injectable({ providedIn: 'root' })
export class PlacementHttpClient {
  protected readonly httpApi = inject(HttpApiService);

  public create({
    offerId,
    candidateId,
    locationIds,
    talentPoolId,
  }: {
    offerId?: number;
    talentPoolId?: number;
    candidateId: number;
    locationIds?: number[] | null;
  }): Observable<{ placement: Placement; references: PlacementHttpReference[] }> {
    return this.httpApi.post(`/placements`, { offerId, talentPoolId, candidateId, locationIds });
  }

  public delete({
    id,
  }: {
    id: number;
  }): Observable<{ placement: Placement; references: PlacementHttpReference[] }> {
    return this.httpApi.delete(`/placements/${id}`);
  }

  public updatePositions(
    positions: Position[],
  ): Observable<{ placements: Placement[]; references: PlacementHttpReference[] }> {
    return this.httpApi.patch(`/placements/update_positions`, null, {
      placements: positions,
    });
  }

  public updateCandidatePositions(
    positions: Position[],
  ): Observable<{ placements: Placement[]; references: PlacementHttpReference[] }> {
    return this.httpApi.patch(`/placements/update_candidate_positions`, null, {
      placements: positions,
    });
  }

  public requalify(
    id: number,
  ): Observable<{ placement: Placement; references: PlacementHttpReference[] }> {
    return this.httpApi.patch(`/placements/${id}/requalify`);
  }

  public disqualify({
    id,
    disqualifyReasonId,
    runActions = null,
  }: PlacementDisqualifyParams): Observable<{
    placement: Placement;
    references: PlacementHttpReference[];
  }> {
    return this.httpApi.patch(`/placements/${id}/disqualify`, {
      disqualifyReasonId,
      ...(runActions !== null && { runActions }),
    });
  }

  public updatePreferredLocations(
    placementId: number,
    locationIds: number[],
  ): Observable<{ placement: Placement; references: PlacementHttpReference[] }> {
    return this.httpApi.patch(`/placements/${placementId}/locations`, null, {
      placement: { locationIds },
    });
  }

  public changeStage({
    id,
    stageId,
    runActions = null,
    proceed = false,
    position,
    hiredAt = '',
    jobStartsAt = '',
    openingId = null,
    workLocationId = null,
    disqualifyReasonId,
    disqualifyRunActions,
    removeFromSharingContainers,
  }: PlacementChangeStageParams): Observable<{
    placement: Placement;
    references: PlacementHttpReference[];
  }> {
    return this.httpApi.patch(`/placements/${id}/change_stage`, {
      stage_id: stageId,
      ...(position != null && { position }),
      ...(runActions !== null && { run_actions: runActions }),
      proceed,
      hiredAt,
      jobStartsAt,
      openingId,
      workLocationId,
      disqualifyReasonId,
      disqualifyRunActions,
      removeFromSharingContainers,
    });
  }

  public changePosition({
    id,
    position,
    proceed,
  }: {
    id: number;
    position?: number;
    proceed?: boolean;
  }): Observable<{ placement: Placement; references: PlacementHttpReference[] }> {
    return this.httpApi.patch(`/placements/${id}/change_position`, {
      ...(position != null && { position }),
      ...(proceed && { proceed }),
    });
  }

  public fetchHiringDetails(placementId: number): Observable<{
    placement: Placement;
    references: PlacementHttpReference[];
  }> {
    return this.httpApi.get(`/placements/${placementId}/hiring_details`);
  }

  public updateHiringDetails({
    placementId,
    hiredAt,
    jobStartsAt,
    openingId,
    workLocationId,
  }: UpdateHiringDetailsParams): Observable<{
    placement: Placement;
    references: PlacementHttpReference[];
  }> {
    return this.httpApi.patch(`/placements/${placementId}/hiring_details`, null, {
      placement: {
        hiredAt,
        ...(jobStartsAt?.length ? { jobStartsAt } : {}),
        openingId,
        workLocationId,
      },
    });
  }
}
